import React, { FC, useEffect, PropsWithChildren, useState } from 'react';
import { observer } from 'mobx-react-lite';
import useStore from '../../hooks/useStore';
import AuthenticationStore from '../../stores/AuthenticationStore';
import useContentLoader from '../../hooks/useContentLoader';
import EmailNotVerifiedModal from './authentication-features/EmailNotVerifiedModal';
import { UserRole } from '../../swagger/api';
import { appInsights } from '../../utils/appInsights';
import RedirectMessageScreen from '../frontpage-features/private-frontpage/components/AppRedirectMessage';
import { AuthHandlerFactory, AuthenticationConfig } from './auth-handlers';
import useAuthenticationDataLoader from './hooks/useAuthenticationDataLoader';

const Authenticator: FC<PropsWithChildren<{}>> = ({ children }) => {
  const authenticationStore = useStore(AuthenticationStore) as AuthenticationStore;
  const { userStore, manageUserAccessStore, notificationStore } = authenticationStore.rootStore;
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);

  const { loadData, handleMitIdRedirect } = useAuthenticationDataLoader({
    authenticationStore,
    userStore,
    manageUserAccessStore,
    notificationStore
  });

  const isAccountFetchedOrAwaitingAccountCreationOrHasNotSelectAccount =
    !!userStore.currentAccount ||
    userStore.person?.signUpInProgress === true ||
    userStore.isSelectAccountShown ||
    userStore.person?.role === UserRole.ProductAccess;

  const isContentLoaded = useContentLoader();

  const initializeAuthentication = async () => {
    const searchParams = new URLSearchParams(document.location.search);

    try {
      const authConfig: AuthenticationConfig = {
        domain: process.env.REACT_APP_AUTH_DOMAIN!,
        clientId: process.env.REACT_APP_AUTH_CLIENT_ID!,
        audience: process.env.REACT_APP_AUTH_AUDIENCE,
        redirectUri: window.location.origin
      };

      const handler = AuthHandlerFactory.createAuthHandler(authConfig);
      const result = await handler.initialize();

      authenticationStore.setIsAuthenticated(result.isAuthenticated);
      authenticationStore.setIsAccessDenied(result.isAccessDenied || false);

      if (result.auth0Client) {
        authenticationStore.setAuthContext(result.auth0Client);
      }

      setShowRedirectMessage(result.shouldShowRedirectMessage || false);

      if (result.isAuthenticated) {
        await handleMitIdRedirect(searchParams);
      }

      return result.isAuthenticated;
    } catch (error) {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error))
      });
      authenticationStore.setIsAuthenticated(false);
      return false;
    }
  };

  useEffect(() => {
    if (!authenticationStore.isAuthenticated) {
      initializeAuthentication().then((isAuthenticated) => {
        if (isAuthenticated) {
          loadData();
        }
      });
    }
  }, []);

  return (
    <>
      {showRedirectMessage && <RedirectMessageScreen />}
      {authenticationStore.isAuthenticated &&
        userStore.person &&
        isContentLoaded &&
        isAccountFetchedOrAwaitingAccountCreationOrHasNotSelectAccount &&
        children}
      {authenticationStore.isAccessDenied && <EmailNotVerifiedModal />}
    </>
  );
};

export default observer(Authenticator);
